/* eslint-disable react-hooks/exhaustive-deps */
import CustomDrawer from '@efex-components/common/Drawer';
import { ClientsResponse } from '@efex-services/client/types';
import { Button } from '@efexteam/components';
import {
  Subjects,
  Actions,
  Can,
  usePermission,
} from '@efexteam/permission-manager';
import useUserValidations from '@main-hooks/useUserValidations';
import { Box, Pagination, Typography, useMediaQuery } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import DataLoadingWrapper from '../../components/common/DataLoadingWrapper';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import OutletTableSkeleton from '../../components/common/Skeleton/OutletTableSkeleton';
import SearchTools from '../../components/searchTools/SearchTools';
import useBoolean from '../../hooks/useBoolean';
import ClientsTable from '../../modules/clients/ClientsTable';
import DeleteClientModal from '../../modules/clients/DeleteClientModal';
import { QueryParams } from '../../services/types';
import { useAppDispatch } from '../../store';
import { selectProfileData } from '../profile/store/selectors';
import AddContactForm from './AddContactForm';
import { FormProvider } from './AddContactForm/context';
import ViewClientDrawer from './components/ViewClientDrawer';
import { INITIAL_STATE } from './config';
import { fetchClients } from './store';
import { selectClientsPaginate, selectClientsState } from './store/selectors';

const Clients = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const clientsState = useSelector(selectClientsState);
  const pagination = useSelector(selectClientsPaginate);
  const userProfile = useSelector(selectProfileData);
  const { isUserValid, redirectToOnboarding } = useUserValidations();
  const matches = useMediaQuery('(min-width:600px)');
  const [width, setWidth] = useState(matches ? '40%' : '100%');
  const [searchParams] = useSearchParams();
  const [renderClients, setRenderClients] = useState(
    clientsState.data?.results
  );
  const [modalInsert, setModalInsert] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [params, setParams] = useState<QueryParams>({});
  const { can } = usePermission();

  const handleResize = (fullWidth: boolean) => {
    if (!matches) {
      setWidth(matches ? '40%' : '100%');
      return;
    }
    setWidth(fullWidth ? '100%' : '40%');
  };

  const [
    isModalDeleteOpen,
    { setFalse: hideModalDelete, setTrue: showModalDelete },
  ] = useBoolean(false);
  const [
    isViewClientOpen,
    { setFalse: hideViewClient, setTrue: showViewClient },
  ] = useBoolean(false);

  const handleChangePage = (_: any, page: number) => {
    if (page === currentPage) return;
    setCurrentPage(page);
    dispatch(fetchClients({ ...params, page }));
  };
  const countryData = useMemo(
    () => (userProfile?.company.country === 'USA' ? 'MX' : 'USA'),
    [userProfile?.company.country]
  );

  const [clientSelected, setClientSelected] = useState<
    ClientsResponse['results'][0] | null
  >({
    ...INITIAL_STATE,
    country: countryData,
  });

  const selectElement = (
    element: ClientsResponse['results'][0],
    useCase: 'Eliminar' | 'View'
  ) => {
    if (useCase === 'Eliminar') {
      showModalDelete();
    } else {
      showViewClient();
    }
    setClientSelected(element);
  };

  const openPopupNewClient = () => {
    setClientSelected(null);
    setModalInsert(true);
  };

  const fetchClientsOnModalClose = () => {
    setModalInsert(false);
    dispatch(fetchClients(params));
  };

  const handleAddNewBusiness = () => {
    if (!isUserValid) {
      redirectToOnboarding();
    } else {
      openPopupNewClient();
    }
  };

  useEffect(() => {
    if (searchParams.get('action') === 'new_client') {
      openPopupNewClient();
    }
  }, []);

  useEffect(() => {
    dispatch(fetchClients(params));
  }, [dispatch, params]);

  useEffect(() => {
    const results = clientsState.data?.results || [];
    setRenderClients([...results].filter((client) => client.id !== null));
  }, [clientsState.data?.results]);

  const renderContent = () => (
    <Box>
      <Box className="flex-col">
        <Box className="block">
          <Typography variant="h6">{t('contacts')}</Typography>
        </Box>
        <Box className="flex flex-row flex-wrap justify-between items-center gap-2 w-full mb-7">
          <Box className="flex flex-row grow max-w-[300px]">
            <SearchTools
              onChange={(search) => setParams({ ...params, search })}
            />
          </Box>
          <Can action={Actions.CREATE} subject={Subjects.BENEFICIARIES}>
            <Box className="button grow max-w-[250px]">
              <Button
                appearance="primary"
                type="button"
                onClick={handleAddNewBusiness}
              >
                {t('addNewBusiness')}
              </Button>
            </Box>
          </Can>
        </Box>
      </Box>

      <DataLoadingWrapper
        loadingData={clientsState.loading}
        customLoader={<OutletTableSkeleton />}
      >
        {!renderClients?.length ? (
          <EmptyState
            onSubmit={
              can(Actions.CREATE, Subjects.BENEFICIARIES)
                ? handleAddNewBusiness
                : undefined
            }
            submitLabel={
              can(Actions.CREATE, Subjects.BENEFICIARIES)
                ? `+ ${t('addNewBusiness')}`
                : undefined
            }
            page="clients"
            roleId={userProfile?.legalRepresentative.roleId}
            showTitle={false}
          />
        ) : (
          <ClientsTable
            clients={renderClients || []}
            onSelect={selectElement}
          />
        )}
      </DataLoadingWrapper>
    </Box>
  );

  return (
    <Box className="min-h-[80vh] p-5">
      {renderContent()}
      {Boolean(Number(renderClients?.length) > 1) && (
        <Box className="flex justify-center p-4 text-center w-full">
          <Pagination
            page={currentPage}
            color="primary"
            count={pagination?.totalPages}
            onChange={handleChangePage}
          />
        </Box>
      )}
      {modalInsert && (
        <FormProvider>
          <AddContactForm
            open={modalInsert}
            onClose={fetchClientsOnModalClose}
          />
        </FormProvider>
      )}

      {isModalDeleteOpen && clientSelected && (
        <DeleteClientModal
          open={isModalDeleteOpen}
          onClose={hideModalDelete}
          client={clientSelected}
        />
      )}
      {isViewClientOpen && clientSelected && (
        <CustomDrawer
          position="right"
          isOpen={isViewClientOpen}
          onClose={hideViewClient}
          PaperProps={{
            sx: {
              width,
            },
          }}
          title={`${clientSelected.contactNaturalPersonEnterprise.naturalPerson.firstName} - ${clientSelected.enterprise.name}`}
          variant="persistent"
          onFullWidth={handleResize}
        >
          <ViewClientDrawer contact={clientSelected} />
        </CustomDrawer>
      )}
    </Box>
  );
};

export default Clients;
